/**
 * Photographs of families (generated from docs/2026-09-28-nurture-image-prompts.md). Drop a
 * `<name>.webp` (or .jpg/.png) into src/assets/photos and it is picked up at build time; until
 * then every slot shows its stand-in illustration, so pages never wait on a photo.
 *
 * Rule: photos are mood images. Never pair one with a name, a quote or a rating.
 */
import { sceneImage, type SceneName } from './illustrations';

const files = import.meta.glob('../assets/photos/*.{webp,png,jpg,jpeg}', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const byName = new Map<string, string>();
for (const [path, url] of Object.entries(files)) {
  const m = /photos\/([^/]+)\.(?:webp|png|jpe?g)$/.exec(path);
  if (m) byName.set(m[1]!, url);
}

/** Every photo slot on the site, with the illustration shown until the photo exists. */
const SLOTS = {
  'landing-hero': 'hero-floor-reading',
  'landing-bridge': 'checkpoint-chai',
  'landing-tonight': 'checkpoint-chai',
  'landing-report-table': 'report-ready-lap',
  'landing-doctor': 'report-ready-lap',
  'landing-between': 'room-table-time',
  'landing-review': 'room-floor-play',
  'landing-final': 'room-move-outdoors',
  'signin-side': 'hero-floor-reading',
  'home-welcome': 'room-floor-play',
  'home-parent-care': 'checkpoint-chai',
  'home-hands': 'report-ready-lap',
  'trust-hero': 'report-ready-lap',
  'trust-clinicians': 'report-ready-lap',
  'learn-hero': 'hero-floor-reading',
  'learn-autism': 'room-floor-play',
  'learn-adhd': 'room-move-outdoors',
  'learn-learning': 'room-table-time',
  'learn-speech': 'hero-floor-reading',
  'learn-devdelay': 'room-floor-play',
  'learn-sensory': 'room-calm-corner',
  'learn-coordination': 'room-move-outdoors',
  'learn-anxiety': 'room-calm-corner',
  'learn-meltdowns': 'room-calm-corner',
  'learn-sleep': 'room-calm-corner',
  'learn-teen': 'checkpoint-chai',
  'learn-parent-care': 'checkpoint-chai',
  'learn-between': 'room-mealtime',
} as const satisfies Record<string, SceneName>;

export type PhotoName = keyof typeof SLOTS;

/** The photo if it has been added, otherwise its stand-in illustration. */
export const photo = (name: PhotoName): string | undefined => byName.get(name) ?? sceneImage(SLOTS[name]);

/** True once the real photo is in the repo (e.g. to add the "photos are illustrative" note). */
export const hasPhoto = (name: PhotoName): boolean => byName.has(name);

export const anyPhotos = (): boolean => byName.size > 0;
